import React, { useState, useEffect } from "react";

// Coffee Cups configuration
const CLOCK_CUPS = [
  {
    id: "red",
    name: "Canyon Crimson",
    src: "https://cdn.jiro.build/Laounge%20Coffe/images/Cap1.png",
    accent: "#8F382C",
    timeLabel: "08:00 AM",
    labelText: "morning brew",
    left: "50%",
    top: "-7%",
    targetHour: 8,
    targetMinute: 0,
    quote: "Bold, intense, and revitalizing. Fueling the start of your day with premium beans."
  },
  {
    id: "black",
    name: "Obsidian Black",
    src: "https://cdn.jiro.build/Laounge%20Coffe/images/Cap4.png",
    accent: "#262524",
    timeLabel: "09:00 PM",
    labelText: "espresso o'clock",
    left: "107%",
    top: "50%",
    targetHour: 21,
    targetMinute: 0,
    quote: "Rich, mysterious, and deeply aromatic. For the dreamers and night thinkers."
  },
  {
    id: "orange",
    name: "Amber Gold",
    src: "https://cdn.jiro.build/Laounge%20Coffe/images/Cap3.png",
    accent: "#D68C45",
    timeLabel: "04:00 PM",
    labelText: "golden hour",
    left: "50%",
    top: "107%",
    targetHour: 16,
    targetMinute: 0,
    quote: "Sweet, warm caramel undertones to carry you through the soft afternoon light."
  },
  {
    id: "green",
    name: "Forest Matcha",
    src: "https://cdn.jiro.build/Laounge%20Coffe/images/Cap2.png",
    accent: "#4D6B54",
    timeLabel: "12:00 PM",
    labelText: "matcha o'clock",
    left: "-7%",
    top: "50%",
    targetHour: 12,
    targetMinute: 0,
    quote: "A perfect balance of earthy matcha and velvet cream to ground your midday focus."
  }
];

export default function CoffeeTime({ className }) {
  const [time, setTime] = useState(new Date());
  const [isInteractive, setIsInteractive] = useState(false);
  const [selectedCupId, setSelectedCupId] = useState(null);

  useEffect(() => {
    if (isInteractive) return;
    const interval = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, [isInteractive]);

  const handleCupHover = (cupId) => {
    if (cupId) {
      setIsInteractive(true);
      setSelectedCupId(cupId);
      const cup = CLOCK_CUPS.find((c) => c.id === cupId);
      if (cup) {
        const demoTime = new Date();
        demoTime.setHours(cup.targetHour, cup.targetMinute, 0);
        setTime(demoTime);
      }
    } else {
      setIsInteractive(false);
      setSelectedCupId(null);
      setTime(new Date());
    }
  };

  const currentHour = time.getHours();
  const currentMinute = time.getMinutes();
  const currentSecond = time.getSeconds();

  const hourDegrees = ((currentHour % 12) * 30) + (currentMinute * 0.5);
  const minuteDegrees = (currentMinute * 6) + (currentSecond * 0.1);
  const secondDegrees = isInteractive ? 0 : currentSecond * 6;

  const getActivePreset = () => {
    if (selectedCupId) {
      return CLOCK_CUPS.find((c) => c.id === selectedCupId);
    }
    if (currentHour >= 5 && currentHour < 11) {
      return CLOCK_CUPS.find((c) => c.id === "red");
    } else if (currentHour >= 11 && currentHour < 15) {
      return CLOCK_CUPS.find((c) => c.id === "green");
    } else if (currentHour >= 15 && currentHour < 19) {
      return CLOCK_CUPS.find((c) => c.id === "orange");
    } else {
      return CLOCK_CUPS.find((c) => c.id === "black");
    }
  };

  const currentTheme = getActivePreset();

  const leftCups = [CLOCK_CUPS[0], CLOCK_CUPS[3]];
  const rightCups = [CLOCK_CUPS[1], CLOCK_CUPS[2]];

  const beans = [
    { angle: 60, id: "bean-2" },
    { angle: 120, id: "bean-4" },
    { angle: 240, id: "bean-8" },
    { angle: 300, id: "bean-10" },
  ];

  const dots = [
    { angle: 30, id: "dot-1" },
    { angle: 150, id: "dot-5" },
    { angle: 210, id: "dot-7" },
    { angle: 330, id: "dot-11" },
  ];

  const renderCup = (cup) => {
    const isSelected = selectedCupId === cup.id;
    return (
      <div
        key={cup.id}
        className="flex flex-col items-center cursor-pointer group transition-all duration-300"
        onMouseEnter={() => handleCupHover(cup.id)}
        onMouseLeave={() => handleCupHover(null)}
        onClick={() => handleCupHover(cup.id)}
      >
        <div className="relative transition-transform duration-300 transform group-hover:-translate-y-2 group-hover:scale-105">
          <div
            className="absolute bottom-1 left-1/2 -translate-x-1/2 w-10 h-2 bg-black/15 rounded-full blur-[4px] -z-10 transition-opacity duration-300"
            style={{ opacity: isSelected ? 0.8 : 0.4 }}
          />
          <img
            src={cup.src}
            alt={cup.name}
            className={"w-16 sm:w-20 md:w-24 h-auto drop-shadow-xl select-none transition-all duration-500 " + (selectedCupId && !isSelected ? "opacity-50 grayscale-[20%]" : "opacity-100")}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@700&display=swap" rel="stylesheet" />

      <section
        id="time-to-coffe-section"
        className={"relative w-full py-16 sm:py-24 px-6 sm:px-12 md:px-16 flex flex-col items-center justify-center overflow-hidden transition-colors duration-1000 " + (className || "")}
        style={{ backgroundColor: "#FAF4EA" }}
      >
        <div
          className="absolute top-1/4 left-1/4 w-[350px] h-[350px] sm:w-[500px] sm:h-[500px] rounded-full blur-[140px] pointer-events-none transition-colors duration-1000 opacity-[0.08]"
          style={{ backgroundColor: currentTheme.accent }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] sm:w-[550px] sm:h-[550px] rounded-full blur-[140px] pointer-events-none transition-colors duration-1000 opacity-[0.06]"
          style={{ backgroundColor: currentTheme.accent }}
        />

        <div className="w-full max-w-[1300px] flex flex-col items-center justify-center gap-12 sm:gap-16 relative z-10">

          <div className="w-full text-center flex flex-col items-center gap-3">
            <h2 className="text-3xl sm:text-5xl md:text-[56px] font-extrabold text-[#2B170A] uppercase tracking-tight leading-none mt-2" style={{ fontFamily: "Inter, sans-serif" }}>
              Brewing Every Second
            </h2>
            <p className="text-sm sm:text-base text-[#2B170A]/70 max-w-xl mt-1" style={{ fontFamily: "Inter, sans-serif" }}>
              Experience our organic timeline. Watch the lounge landscape transition gracefully through distinct coffee moments of the day.
            </p>
          </div>

          <div className="w-full flex flex-col items-center justify-center">
            <div className="w-full max-w-[1100px] flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 lg:gap-24 relative py-12 px-4">

              <div className="flex md:flex-col justify-around md:justify-between items-center w-full md:w-auto h-auto md:h-[320px] lg:h-[360px] gap-6 md:gap-0">
                {leftCups.map((cup) => renderCup(cup))}
              </div>

              <div className="relative w-[230px] sm:w-[380px] md:w-[440px] h-[230px] sm:h-[380px] md:h-[440px] flex items-center justify-center select-none shrink-0">

                <div className="absolute w-[114%] h-[114%] rounded-full border border-dashed border-[#2B170A]/15 flex items-center justify-center z-0 pointer-events-none left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">

                  {beans.map((bean) => {
                    const angleRad = (bean.angle * Math.PI) / 180;
                    const leftPos = `calc(50% + 50% * ${Math.sin(angleRad).toFixed(4)})`;
                    const topPos = `calc(50% - 50% * ${Math.cos(angleRad).toFixed(4)})`;

                    return (
                      <div
                        key={bean.id}
                        className="absolute"
                        style={{
                          left: leftPos,
                          top: topPos,
                          transform: `translate(-50%, -50%) rotate(${bean.angle}deg)`,
                        }}
                      >
                        <svg width="14" height="22" viewBox="0 0 14 22" fill="none" opacity="0.35" className="rotate-[12deg]">
                          <path d="M7 1 C10 1 13 5 13 11 C13 17 10 21 7 21 C4 21 1 17 1 11 C1 5 4 1 7 1 Z" fill="#2B170A" />
                          <path d="M1 11 Q7 13 13 11" stroke="#FAF4EA" strokeWidth={1.2} strokeLinecap="round" />
                        </svg>
                      </div>
                    );
                  })}

                  {dots.map((dot) => {
                    const angleRad = (dot.angle * Math.PI) / 180;
                    const leftPos = `calc(50% + 50% * ${Math.sin(angleRad).toFixed(4)})`;
                    const topPos = `calc(50% - 50% * ${Math.cos(angleRad).toFixed(4)})`;

                    return (
                      <div
                        key={dot.id}
                        className="absolute w-[6px] h-[6px] sm:w-[7px] sm:h-[7px] bg-[#2B170A]/35 rounded-full"
                        style={{
                          left: leftPos,
                          top: topPos,
                          transform: "translate(-50%, -50%)",
                        }}
                      />
                    );
                  })}

                </div>

                <div
                  id="main-clock-face"
                  className="relative w-[90%] h-[90%] rounded-full bg-[#FCFAF6] border-8 border-white shadow-[0_20px_50px_rgba(43,23,10,0.08)] flex items-center justify-center overflow-visible z-10 transition-shadow duration-500 hover:shadow-[0_25px_60px_rgba(43,23,10,0.12)]"
                >

                  <div className="absolute left-[24%] sm:left-[26%] md:left-[28%] top-[50%] -translate-y-1/2 flex items-center gap-1.5 pointer-events-none select-none">
                    <span className="text-[12px] sm:text-[14px] text-[#2B170A]/75 font-medium tracking-tight" style={{ fontFamily: "Inter, sans-serif" }}>
                      {currentTheme.labelText}
                    </span>
                    <span
                      className="w-2 h-2 rounded-full transition-colors duration-1000 animate-pulse shrink-0"
                      style={{ backgroundColor: currentTheme.accent }}
                    />
                  </div>

                  <div className="absolute w-full h-full inset-0 pointer-events-none z-20 flex items-center justify-center">

                    <div
                      className="absolute w-[5px] sm:w-[7px] rounded-full origin-bottom transition-transform duration-500 ease-out"
                      style={{
                        backgroundColor: "#2B170A",
                        height: "22%",
                        top: "28%",
                        transform: `rotate(${hourDegrees}deg)`,
                      }}
                    />

                    <div
                      className="absolute w-[3.5px] sm:w-[4.5px] rounded-full origin-bottom transition-transform duration-500 ease-out"
                      style={{
                        backgroundColor: "#2B170A",
                        height: "32%",
                        top: "18%",
                        transform: `rotate(${minuteDegrees}deg)`,
                      }}
                    />

                    <div
                      className="absolute w-[1.2px] sm:w-[1.8px] origin-bottom transition-all duration-300"
                      style={{
                        backgroundColor: currentTheme.accent,
                        height: "38%",
                        top: "12%",
                        transform: `rotate(${secondDegrees}deg)`,
                        opacity: isInteractive ? 0.35 : 1,
                      }}
                    />

                    <div
                      className="absolute w-4 h-4 sm:w-5 sm:h-5 rounded-full border-4 border-[#FCFAF6] shadow-md transition-colors duration-1000"
                      style={{ backgroundColor: currentTheme.accent }}
                    />
                  </div>

                  <div className="absolute bottom-[22%] left-1/2 -translate-x-1/2 pointer-events-none select-none flex flex-col items-center">
                    <span className="text-[10px] sm:text-[11px] font-bold text-[#2B170A]/30 tracking-[0.25em] uppercase" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      Lounge Coffe
                    </span>
                  </div>

                </div>
              </div>

              <div className="flex md:flex-col justify-around md:justify-between items-center w-full md:w-auto h-auto md:h-[320px] lg:h-[360px] gap-6 md:gap-0">
                {rightCups.map((cup) => renderCup(cup))}
              </div>

            </div>
          </div>

        </div>
      </section>
    </>
  );
}